import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import classnames from 'classnames';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import isEqual from 'lodash-es/isEqual';

import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import Toggle from 'react-toggle';

import RelationSearch from '@/shared/components/input/RelationSearch';
import UserSearch from '@/shared/components/input/UserSearch';
import MultiSelect from '@/shared/components/input/multiselect/MultiSelect';
import AuthService from '@/shared/services/AuthService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import guid from '@/shared/utils/guid';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';
import userStorage from '@/shared/services/userStorage';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import TaskIcon from '../TaskIcon';
import messages from '../taskMessages';
import config from '../../../config';
import DynamicTaskFilter, {
  initDynamicFilters,
  getDynamicFiltersFromDefaultFilters,
  resetDynamicFilters,
  handleDynamicFiltersChanged,
} from '../DynamicTaskFilter';
import { CanRead } from '@/shared/components/security';

export const defaultFilters = {
  deadlineFrom: moment.utc().startOf('month'),
  deadlineUntil: moment.utc().endOf('month'),
  executionDate: { from: null, until: null },
  period: 'month',
  relationId: null,
  relationName: null,
  userId: null,
  relationUserId: null,
  relationUsername: null,
  taskTemplateId: null,
  taskId: null, //= Subtask id
  periodicityId: null,
  mainStatus: [],
  relationTypes: [],
  relationTags: [],
  withoutRelationTags: [],
  taskLevels: [],
  ownCompanies: [],
  assistant: null, // UserId
  responsible: null, // UserId
  associate: null, // UserId
  assistantBackup: null, // UserId
  billingUser: null, // UserId
  seniorUser: null, // UserId
  assignmentTemplateId: null,
  endFiscalYears: [],
  accountingSoftware: [],
  receiveInvoiceSoftware: [],
  otherSoftware: [],
  sendInvoiceSoftware: [],
  teams: [],
  year: null,
  plannedTaskId: null,
  vatPeriodicity: [],
  createdBy: null, // UserId
  legalForms: [], // Array
  financialStatementScheme: [],
  socialSecurityFund: [],
  socialSecretariat: [],
  withTags: [],
  withoutTags: [],
  relationGroups: [],
  withRequiredAuthorizationWarning: [],
  filterBehaviours: {
    withTags: 1,
    relationTags: 1,
  },
  mixedTaxPayer: null,
  mixedTaxPayerVatDeductionMethod: [],
};

const getPeriodStartEndDates = period => {
  switch (period) {
    case 'all':
    case '':
    case null:
    case undefined:
      break;
    case 'month':
    case 'quarter':
    case 'year':
      return [moment.utc().startOf(period), moment.utc().endOf(period)];
    default:
      console.debug(`Unknown period: ${period}`);
  }
  return [null, null];
};
const getIgnoredFilters = filters => {
  if (filters.period) {
    // Ignore deadline filters if there is a period
    return ['deadlineUntil', 'deadlineFrom'];
  }
  return [];
};
const allDynamicFilters = [...getDynamicFiltersFromDefaultFilters(defaultFilters)];

const dynamicFiltersStorageKey = 'TaskListDynamicFilters';

class TaskListFilters extends Component {
  state = {
    taskStatuses: [],
    templates: [],
    subTasks: [],
    periodicities: [],
    dynamicFilters: [],
    taskLabels: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      deadlineUntil: momentPropTypes.momentObj,
      deadlineFrom: momentPropTypes.momentObj,
      period: PropTypes.oneOf(['all', 'month', 'quarter', 'year']),
      executionDate: PropTypes.shape({
        from: momentPropTypes.momentObj,
        until: momentPropTypes.momentObj,
      }),
      relationId: PropTypes.string,
      relationName: PropTypes.string,
      userId: PropTypes.string,
      relationUserId: PropTypes.string,
      relationUsername: PropTypes.string,
      taskTemplateId: PropTypes.string,
      taskId: PropTypes.string, //= Subtask id
      periodicityId: PropTypes.string,
      mainStatus: PropTypes.arrayOf(PropTypes.number),
      relationTypes: PropTypes.arrayOf(PropTypes.number),
      relationTags: PropTypes.arrayOf(PropTypes.string),
      withoutRelationTags: PropTypes.arrayOf(PropTypes.string),
      taskLevels: PropTypes.arrayOf(PropTypes.number),
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      responsible: PropTypes.string,
      assistant: PropTypes.string,
      associate: PropTypes.string,
      assistantBackup: PropTypes.string,
      billingUser: PropTypes.string,
      seniorUser: PropTypes.string,
      assignmentTemplateId: PropTypes.string,
      accountingSoftware: PropTypes.arrayOf(PropTypes.number),
      receiveInvoiceSoftware: PropTypes.arrayOf(PropTypes.number),
      otherSoftware: PropTypes.arrayOf(PropTypes.number),
      sendInvoiceSoftware: PropTypes.arrayOf(PropTypes.number),
      createdBy: PropTypes.string,
      legalForms: PropTypes.arrayOf(PropTypes.number),
      withTags: PropTypes.arrayOf(PropTypes.string),
      withoutTags: PropTypes.arrayOf(PropTypes.string),
      relationGroups: PropTypes.arrayOf(PropTypes.string),
      userIsNextAssignedUser: PropTypes.bool,
      WithRequiredAuthorizationWarning: PropTypes.arrayOf(PropTypes.bool),
    }),
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    filters: defaultFilters,
  };

  componentDidMount() {
    const { filters, intl } = this.props;
    const withRequiredAuthorizationWarningFilters = [
      {
        id: false,
        label: intl.formatMessage(messages.WithoutMissingAuthorizations),
      },
      {
        id: true,
        label: intl.formatMessage(messages.WithMissingAuthorizations),
      },
    ];
    const taskStatuses = [
      {
        id: 0,
        label: intl.formatMessage(listItemMsg('extendedtaskstatus', 0, 'To do')),
      },
      {
        id: 1,
        label: intl.formatMessage(listItemMsg('extendedtaskstatus', 1, 'In progress')),
      },
      {
        id: 2,
        label: intl.formatMessage(listItemMsg('extendedtaskstatus', 2, 'Done')),
      },
      {
        id: 3,
        label: intl.formatMessage(listItemMsg('extendedtaskstatus', 3, 'Blocked')),
      },
      {
        id: 4,
        label: intl.formatMessage(listItemMsg('extendedtaskstatus', 4, 'Inapplicable')),
      },
      {
        id: 6,
        label: intl.formatMessage(listItemMsg('extendedtaskstatus', 6, 'Rejected')),
      },
      {
        id: 5,
        label: intl.formatMessage(listItemMsg('extendedtaskstatus', 5, 'Not done')),
      },
    ];
    this.setState({
      taskStatuses,
      withRequiredAuthorizationWarningFilters,
      dynamicFilters: initDynamicFilters(
        intl,
        allDynamicFilters,
        userStorage.getItem(dynamicFiltersStorageKey) || [],
        filters,
        defaultFilters,
      ),
    });

    if (filters && filters.period) {
      this.setPeriod(filters.period);
    }
    api.base.get(`${config.appUrls.api}/api/Resource/Tags`).then(({ data }) => {
      this.setState({
        relationLabels: data
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
          .sort(dynamicSort('sort')),
      });
    });
    api.base.get(`${config.appUrls.tasks}/api/Tags`).then(({ data }) => {
      this.setState({
        taskLabels: data
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
          .sort(dynamicSort('sort')),
      });
    });
    this.retrieveTemplates();
    if (filters.taskTemplateId) {
      this.retrieveSubTasks(filters.taskTemplateId);
      this.retrievePeriodicities(filters.taskTemplateId);
    }
  }

  retrieveTemplates() {
    const { intl } = this.props;
    api.base.get(`${config.appUrls.tasks}/api/TaskTemplates`).then(({ data }) => {
      const translatedTemplates = data
        .map(t => ({
          ...t,
          label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)), // Map translation to label property
        }))
        .sort(dynamicSort('label'));

      this.setState({
        templates: translatedTemplates,
      });
    });
  }
  retrieveSubTasks(taskTemplateId) {
    const { intl } = this.props;
    if (!taskTemplateId || taskTemplateId === guid.empty) {
      this.setState({ subTasks: [] });
      return;
    }

    api.base.get(`${config.appUrls.tasks}/api/TaskTemplates/Children/${taskTemplateId}`).then(
      ({ data }) => {
        const translatedSubtasks = data
          .map(t => ({
            ...t,
            label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)), // Map translation to label property
          }))
          .sort(dynamicSort('label'));

        this.setState({
          subTasks: translatedSubtasks,
        });
      },
      () => {
        this.setState({
          subTasks: [],
        });
      },
    );
  }
  retrievePeriodicities = templateId => {
    if (!templateId || templateId === guid.empty) {
      this.setState({ periodicities: [] });
      return;
    }
    const { intl } = this.props;
    return api.base
      .get(`${config.appUrls.tasks}/api/TaskPeriodicity/${templateId}`)
      .then(({ data }) => {
        const translatedPeriodicities = data
          .map(t => ({
            ...t,
            label: intl.formatMessage(listItemMsg('taskperiodicity', t.id, t.name)), // Map translation to label property
          }))
          .sort(dynamicSort('label'));

        this.setState(state => ({
          ...state,
          periodicities: translatedPeriodicities,
        }));
      });
  };

  dynamicFilterChanged = (item, selected) => {
    this.setState(({ dynamicFilters }) => {
      const { filters } = this.props;
      const newFilters = handleDynamicFiltersChanged(
        dynamicFilters,
        item,
        selected,
        filters,
        defaultFilters,
      );
      userStorage.setItem(
        dynamicFiltersStorageKey,
        newFilters.filter(x => x.selected).map(x => x.id),
      );
      if (
        !selected &&
        item.id !== 'selectAll' &&
        !isEqual(filters[item.id], defaultFilters[item.id])
      ) {
        // Reset filter when it's removed
        this.filterChanged({ [item.id]: defaultFilters[item.id] });
      }
      return { dynamicFilters: newFilters };
    });
  };
  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    onFilterChange(newFilters);
  };

  savedFilterChanged = filters => {
    const { onFilterChange } = this.props;
    const newFilters = { ...defaultFilters, ...filters };
    if (typeof newFilters.deadlineFrom === 'string')
      newFilters.deadlineFrom = moment.utc(filters.deadlineFrom);
    if (typeof newFilters.deadlineUntil === 'string')
      newFilters.deadlineUntil = moment.utc(filters.deadlineUntil);

    if (!!newFilters.executionDate) {
      if (typeof newFilters.executionDate.from === 'string')
        newFilters.executionDate.from = moment.utc(filters.executionDate.from);
      if (typeof newFilters.executionDate.until === 'string')
        newFilters.executionDate.until = moment.utc(filters.executionDate.until);
    }

    if (newFilters.period) {
      const [deadlineFrom, deadlineUntil] = getPeriodStartEndDates(newFilters.period);
      newFilters.deadlineFrom = deadlineFrom;
      newFilters.deadlineUntil = deadlineUntil;
    }

    onFilterChange(newFilters);

    this.setState(({ dynamicFilters }) => ({
      dynamicFilters: resetDynamicFilters(dynamicFilters, newFilters, defaultFilters),
    }));
    const { taskTemplateId } = newFilters;
    this.retrieveSubTasks(taskTemplateId);
    this.retrievePeriodicities(taskTemplateId);
  };

  deadlineChanged = (startDate, endDate) => {
    this.filterChanged({ deadlineFrom: startDate, deadlineUntil: endDate, period: null });
  };

  taskTemplateChanged = taskTemplateId => {
    const { filters: { periodicityId } = {} } = this.props;
    this.filterChanged({
      taskTemplateId,
      taskId: null,
      periodicityId: periodicityId === guid.empty ? periodicityId : null,
    });
    this.retrieveSubTasks(taskTemplateId);
    this.retrievePeriodicities(taskTemplateId);
  };
  relationChanged = item => {
    if (!item || !item.invoiceStreamId) {
      this.filterChanged({ invoiceStreamId: null, relationId: null, relationName: null });
    } else {
      this.filterChanged({
        invoiceStreamId: null,
        relationId: item.key,
        relationName: item.label,
      });
    }
  };

  userChanged = item => {
    this.filterChanged({ userId: item?.id || null });
  };

  relationTypeChanged = value => {
    this.filterChanged({ relationType: value });
  };

  setPeriod = period => {
    const [deadlineFrom, deadlineUntil] = getPeriodStartEndDates(period);
    this.filterChanged({ deadlineFrom, deadlineUntil, period });
  };

  onStatusFilterRemoved = item => {
    const { filters } = this.props;
    const { mainStatus } = filters;
    const index = mainStatus.findIndex(x => x === item.value.id);

    const copy = [...mainStatus];
    copy.splice(index, 1);

    this.filterChanged({ mainStatus: copy });
  };

  onStatusFilterAdded = selectedItem => {
    if (!selectedItem) return;

    const { filters } = this.props;
    const { mainStatus } = filters;

    if (mainStatus.includes(selectedItem.id)) return;
    this.filterChanged({
      mainStatus: [...mainStatus, selectedItem.id],
    });
  };

  filterTaskLevelChanged = taskLevel => {
    const { filters } = this.props;
    const { taskLevels } = filters;

    let newTaskLevels = [];

    if (taskLevels.includes(taskLevel)) {
      newTaskLevels = taskLevels.filter(x => x !== taskLevel);
    } else {
      newTaskLevels = [...taskLevels, taskLevel];
    }

    this.filterChanged({
      taskLevels: newTaskLevels,
    });
  };

  parseBoolean = value => {
    return value === 'false' || value === '0' || value === 'no' ? false : !!value;
  };

  renderCustomFilter = ({ type, filters, intl }) => {
    switch (type) {
      case 'withRequiredAuthorizationWarning': {
        const { withRequiredAuthorizationWarningFilters } = this.state;

        return (
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({
                    withRequiredAuthorizationWarning: e.target.value
                      ? [this.parseBoolean(e.target.value)]
                      : [],
                  })
                }
                value={
                  filters.withRequiredAuthorizationWarning?.length
                    ? filters.withRequiredAuthorizationWarning[0]
                    : ''
                }
              >
                <option value="">
                  {intl.formatMessage(messages.WithoutRequiredAuthorizationWarningFilter)}
                </option>
                {withRequiredAuthorizationWarningFilters.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }

      default:
        console.warn(`Missing filter: ${type}`);
        return null;
    }
  };

  render() {
    const {
      taskStatuses,
      relationLabels,
      taskLabels,
      templates,
      subTasks,
      periodicities,
      dynamicFilters,
    } = this.state;
    const { filters, intl, countSelectedTasks } = this.props;
    const defaultBtnClass = 'btn btn-default';

    let initRelation = null;

    if (filters.relationId && filters.relationName) {
      initRelation = {
        key: filters.relationId,
        label: filters.relationName,
        invoiceStreamId: filters.invoiceStreamId,
      };
    }
    // transform filter (array of task status integers to list of object with label and id)
    const selectedStatuses = taskStatuses
      ? taskStatuses.filter(r => filters.mainStatus.includes(r.id))
      : [];

    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);

    return (
      <div className="info-block container-fluid">
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <RelationSearch onChange={this.relationChanged} initialSelectedItem={initRelation} />
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <UserSearch
                onChange={this.userChanged}
                value={filters.userId}
                placeholder={intl.formatMessage(messages.TasksAssignedTo)}
                withCurrentUser
                withUnassigned
                unassignedId={guid.empty}
              />
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <div role="group" aria-label="period" className="btn-group btn-group-justified">
                <CanRead on="Todo">
                  <button
                    onClick={() => {
                      document.activeElement.blur();
                      this.filterTaskLevelChanged(2);
                    }}
                    type="button"
                    title={intl.formatMessage(messages.FilterOnToDos)}
                    className={classnames(defaultBtnClass, {
                      active: filters.taskLevels.includes(2),
                    })}
                  >
                    <TaskIcon taskLevel={2} />
                    <FormattedMessage {...messages.ToDos} />
                  </button>
                </CanRead>

                <CanRead on="PlannedTask">
                  <button
                    onClick={() => {
                      document.activeElement.blur();
                      this.filterTaskLevelChanged(0);
                    }}
                    type="button"
                    title={intl.formatMessage(messages.FilterOnMainTasks)}
                    className={classnames(defaultBtnClass, {
                      active: filters.taskLevels.includes(0),
                    })}
                  >
                    <TaskIcon taskLevel={0} />
                    <FormattedMessage {...messages.MainTasks} />
                  </button>

                  <button
                    title={intl.formatMessage(messages.FilterOnSubTasks)}
                    onClick={() => {
                      document.activeElement.blur();
                      this.filterTaskLevelChanged(1);
                    }}
                    type="button"
                    className={classnames(defaultBtnClass, {
                      active: filters.taskLevels.includes(1),
                    })}
                  >
                    <TaskIcon taskLevel={1} />
                    <FormattedMessage {...messages.SubTasks} />
                  </button>
                </CanRead>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <div role="group" aria-label="period" className="btn-group btn-group-justified">
                <button
                  onClick={() => this.setPeriod('month')}
                  type="button"
                  className={classnames(defaultBtnClass, { active: filters.period === 'month' })}
                >
                  <FormattedMessage {...messages.ThisMonth} />
                </button>
                <button
                  onClick={() => this.setPeriod('quarter')}
                  type="button"
                  className={classnames(defaultBtnClass, { active: filters.period === 'quarter' })}
                >
                  <FormattedMessage {...messages.ThisQuarter} />
                </button>
                <button
                  onClick={() => this.setPeriod('year')}
                  type="button"
                  className={classnames(defaultBtnClass, { active: filters.period === 'year' })}
                >
                  <FormattedMessage {...messages.ThisYear} />
                </button>
                <button
                  onClick={() => this.setPeriod('all')}
                  type="button"
                  className={classnames(defaultBtnClass, {
                    active: !filters.deadlineFrom && !filters.deadlineUntil,
                  })}
                >
                  <FormattedMessage {...messages.All} />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3 margin5B">
            <div className="form-group">
              {templates && (
                <select
                  onChange={e => this.taskTemplateChanged(e.target.value || null)}
                  className="form-control"
                  value={filters.taskTemplateId || ''}
                >
                  <option value="">{intl.formatMessage(messages.AllTasks)}</option>
                  <option value={guid.empty}>
                    {intl.formatMessage(messages.TasksWithoutTemplate)}
                  </option>
                  <option
                    style={{ fontSize: '1pt' }}
                    className="grey"
                    disabled="disabled"
                    value="&nbsp;"
                  >
                    &nbsp;
                  </option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label
                className="react-toggle-wrapper margin10T"
                title={intl.formatMessage(messages.ShowTasksWhereSelectedUserIsNextInLineTooltip)}
              >
                <Toggle
                  checked={filters.userIsNextAssignedUser}
                  onChange={e =>
                    this.filterChanged({
                      userIsNextAssignedUser: !filters.userIsNextAssignedUser,
                      // when enabling this filter and no userId is filtered, set the userId to the current user
                      userId:
                        !filters.userIsNextAssignedUser === true && !filters.userId
                          ? AuthService.getUserId()
                          : filters.userId,
                    })
                  }
                />
                <FormattedMessage
                  className="label-text"
                  {...messages.ShowTasksWhereSelectedUserIsNextInLine}
                />
              </label>
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <select
                onChange={e => this.filterChanged({ periodicityId: e.target.value || null })}
                className="form-control"
                value={filters.periodicityId || ''}
              >
                <option value="">
                  {filters.periodicityId
                    ? intl.formatMessage(messages.ClearFilter)
                    : intl.formatMessage(messages.FilterPeriodicity)}
                </option>
                <option value={guid.empty}>{intl.formatMessage(messages.NoPeriodicity)}</option>
                {periodicities && !!periodicities.length && (
                  <option
                    style={{ fontSize: '1pt' }}
                    className="grey"
                    disabled="disabled"
                    value="&nbsp;"
                  >
                    &nbsp;
                  </option>
                )}
                {periodicities.map(periodicity => (
                  <option key={periodicity.id} value={periodicity.id}>
                    {periodicity.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.deadlineFrom}
                startDateId="TaskOverviewFilterDeadlineFrom"
                endDate={filters.deadlineUntil}
                endDateId="TaskOverviewFilterDeadlineUntil"
                onDatesChange={({ startDate, endDate }) => this.deadlineChanged(startDate, endDate)}
                startDatePlaceholderText={intl.formatMessage(messages.DeadlineFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.DeadlineUntil)}
                block
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3">
            <div className="form-group">
              {subTasks && (
                <select
                  onChange={e => this.filterChanged({ taskId: e.target.value || null })}
                  className="form-control"
                  disabled={!subTasks.length}
                  value={filters.taskId || ''}
                >
                  <option value="">{intl.formatMessage(messages.SelectASubtask)}</option>
                  {subTasks.map(sub => (
                    <option key={sub.id} value={sub.id}>
                      {sub.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedStatuses}
                onItemRemoved={this.onStatusFilterRemoved}
                onItemSelected={this.onStatusFilterAdded}
                items={taskStatuses}
                placeholder={intl.formatMessage(messages.TaskStatus)}
              />
            </div>
          </div>
          {visibleFilters && visibleFilters.length > 0
            ? visibleFilters.map(id => (
                <DynamicTaskFilter
                  key={id}
                  type={id}
                  intl={intl}
                  taskLabels={taskLabels}
                  labels={relationLabels}
                  filters={filters}
                  filterChanged={this.filterChanged}
                  renderCustomFilter={this.renderCustomFilter}
                  columnClassName="col-md-3 margin5B"
                />
              ))
            : null}
        </div>

        <div className="row">
          <div className="col-md-10">
            <SavedFiltersButton
              screen="TaskList"
              currentFilters={filters}
              defaultFilters={defaultFilters}
              onFiltersChanged={this.savedFilterChanged}
              ignoreFilters={getIgnoredFilters(filters)}
              dynamicFilters={dynamicFilters}
              onDynamicFiltersChanged={this.dynamicFilterChanged}
            />
          </div>
          <div className="margin5B content-right col-md-2">
            <span className="margin-10B pull-bottom">{countSelectedTasks}</span>
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(TaskListFilters);
